import { createContext, useContext, useMemo, useEffect, useState } from 'react';
import once from 'transmute/once';
import getIn from 'transmute/getIn';
import Raven from 'raven-js';
import { useFetchFrameworkAppSettings } from 'framework-listing-lib/internal/frameworkAppSettings/hooks/useFrameworkAppSettings';
import useParseViews from 'framework-listing-lib/internal/ViewTabs/hooks/useParseViews';
import usePerformanceTracking from 'framework-listing-lib/internal/hooks/usePerformanceTracking';
import { MEASURE_MARK_NAMES } from 'framework-listing-lib/internal/utils/performanceTracking';
import useFetchListingLibData from '../hooks/useFetchListingLibData';
import { clearQuickFetchErrorsFromWindow, getQuickFetchErrorsFromWindow } from '../../earlyRequester/utils';
import { jsx as _jsx } from "react/jsx-runtime";
const DataContext = /*#__PURE__*/createContext(undefined);
const trackErrors = once(() => {
  // Track any errors we encountered during the quick-fetches, except timeouts as we can't fix those.
  getQuickFetchErrorsFromWindow().forEach(error => {
    const status = getIn(['xhttp', 'status'], error);
    if (status === 0) {
      return;
    }
    Raven.captureException(error, {
      tags: {
        // @ts-expect-error ts-migrate(2322) FIXME: Type 'boolean' is not assignable to type 'string'.
        fromQuickFetch: true
      },
      extra: {
        response: getIn(['xhttp', 'response'], error),
        responseText: getIn(['xhttp', 'responseText'], error),
        responseURL: getIn(['xhttp', 'responseURL'], error),
        status
      }
    });
  });
  clearQuickFetchErrorsFromWindow();
});
export default function DataProvider({
  children,
  showHighlySensitiveProperties
}) {
  const [globalError, setGlobalError] = useState({});
  const viewsPerformanceTracking = usePerformanceTracking(MEASURE_MARK_NAMES.VIEWS);
  const {
    data: appSettings,
    loading: loadingAppSettings
  } = useFetchFrameworkAppSettings();
  const {
    loading: loadingListingLibData,
    properties,
    propertyGroups,
    typeDefinitions
  } = useFetchListingLibData({
    showHighlySensitiveProperties
  });
  const {
    loading: loadingViews,
    views,
    pinnedViews
  } = useParseViews();
  const loading = useMemo(() => loadingAppSettings || loadingListingLibData || loadingViews, [loadingAppSettings, loadingListingLibData, loadingViews]);
  useEffect(() => {
    if (!loading) {
      trackErrors();
    }
  }, [loading]);
  useEffect(() => {
    if (loadingViews) {
      return;
    }
    viewsPerformanceTracking.markTimeEnd();
  }, [loadingViews, viewsPerformanceTracking]);
  const value = useMemo(() => {
    return {
      appSettings,
      globalError,
      loading,
      pinnedViews,
      setGlobalError,
      typeDefinitions,
      views,
      properties,
      propertyGroups
    };
  }, [appSettings, globalError, loading, pinnedViews, properties, propertyGroups, typeDefinitions, views]);
  return /*#__PURE__*/_jsx(DataContext.Provider, {
    value: value,
    children: children
  });
}
export function useListingLibData() {
  const context = useContext(DataContext);
  if (context === undefined) {
    throw new Error('useListingLibData must be used within a DataProvider');
  }
  return context;
}
export function useGlobalError() {
  const {
    globalError,
    setGlobalError
  } = useListingLibData();
  return [globalError, setGlobalError];
}